/** Opaque hashes from Vinalfa — pass through unchanged. */

export interface VinalfaModification {
  vehicleHash: string;
  name?: string;
  title?: string;
  description?: string;
  brand?: string;
  model?: string;
  year?: string | number;
  engine?: string;
  catalog?: string;
  [key: string]: unknown;
}

export interface VinalfaCatalog {
  name?: string;
  brand?: string;
  modifications?: VinalfaModification[];
  vehicles?: VinalfaModification[];
  [key: string]: unknown;
}

export interface VinalfaResolveResponse {
  data?: {
    catalogs?: VinalfaCatalog[];
    vehicles?: VinalfaModification[];
    [key: string]: unknown;
  };
  meta?: unknown;
  catalogs?: VinalfaCatalog[];
  [key: string]: unknown;
}

export interface VinalfaGroup {
  hash?: string;
  sectionHash?: string;
  name?: string;
  title?: string;
  image?: string;
  imageUrl?: string;
  hasChildren?: boolean;
  hasDiagram?: boolean;
  children?: VinalfaGroup[];
  [key: string]: unknown;
}

export interface VinalfaGroupsResponse {
  data?: VinalfaGroup[] | { groups?: VinalfaGroup[]; items?: VinalfaGroup[] };
  groups?: VinalfaGroup[];
  items?: VinalfaGroup[];
  [key: string]: unknown;
}

export interface VinalfaChildrenResponse {
  data?:
    | VinalfaGroup[]
    | { children?: VinalfaGroup[]; sections?: VinalfaGroup[]; diagrams?: VinalfaGroup[] };
  children?: VinalfaGroup[];
  sections?: VinalfaGroup[];
  diagrams?: VinalfaGroup[];
  items?: VinalfaGroup[];
  [key: string]: unknown;
}

export interface VinalfaHotspot {
  ref?: string | number;
  number?: string | number;
  x?: number;
  y?: number;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  [key: string]: unknown;
}

export interface VinalfaPart {
  ref?: string | number;
  position?: string | number;
  number?: string;
  articleNumber?: string;
  oem?: string;
  name?: string;
  description?: string;
  qty?: number | string;
  quantity?: number | string;
  note?: string;
  replacedBy?: string;
  [key: string]: unknown;
}

export interface VinalfaPartsResponse {
  data?: {
    image?: string;
    imageUrl?: string;
    diagram?: string;
    hotspots?: VinalfaHotspot[];
    coordinates?: VinalfaHotspot[];
    parts?: VinalfaPart[];
    positions?: VinalfaPart[];
    [key: string]: unknown;
  };
  image?: string;
  imageUrl?: string;
  diagram?: string;
  hotspots?: VinalfaHotspot[];
  parts?: VinalfaPart[];
  [key: string]: unknown;
}

/** Flatten resolve response into selectable vehicle rows for the modal. */
export function flattenVinalfaVehicles(
  payload: VinalfaResolveResponse | null | undefined
): VinalfaModification[] {
  if (!payload) return [];
  const rows: VinalfaModification[] = [];

  const pushMod = (mod: any, catalogName?: string) => {
    if (!mod || typeof mod !== "object") return;
    const hash =
      mod.vehicleHash || mod.hash || mod.vehicle_hash;
    if (!hash) return;
    rows.push({
      ...mod,
      vehicleHash: String(hash),
      catalog: catalogName || mod.catalog,
      name:
        mod.name ||
        mod.title ||
        mod.model ||
        [mod.brand, mod.model, mod.engine].filter(Boolean).join(" "),
      engine: mod.engine || mod.engine_code,
      year: mod.year || mod.date || mod.model_year,
    } as VinalfaModification);
  };

  // Observed Vinalfa shape:
  // { data: { catalog, vin, data: { modifications: [{ hash, model, engine, ... }] } } }
  const root: any = payload.data || payload;
  const nestedData = root?.data && typeof root.data === "object" ? root.data : null;
  const catalogLabel =
    root?.catalog || root?.catalog_name || nestedData?.catalog || undefined;

  const directMods =
    nestedData?.modifications ||
    root?.modifications ||
    nestedData?.vehicles ||
    root?.vehicles;
  if (Array.isArray(directMods)) {
    for (const m of directMods) pushMod(m, catalogLabel);
  }

  const catalogs =
    payload.data?.catalogs ||
    (payload as any).catalogs ||
    [];
  if (Array.isArray(catalogs)) {
    for (const cat of catalogs) {
      const mods = cat.modifications || cat.vehicles || [];
      if (Array.isArray(mods)) {
        for (const m of mods) pushMod(m, cat.name || cat.brand || catalogLabel);
      }
    }
  }

  const topVehicles = (payload.data as any)?.vehicles;
  if (Array.isArray(topVehicles)) {
    for (const m of topVehicles) pushMod(m, catalogLabel);
  }

  // Deduplicate by vehicleHash
  const seen = new Set<string>();
  return rows.filter((r) => {
    if (seen.has(r.vehicleHash)) return false;
    seen.add(r.vehicleHash);
    return true;
  });
}

/** Pick first array among known Vinalfa list keys on an object. */
function pickVinalfaArray(obj: Record<string, unknown> | null | undefined): VinalfaGroup[] {
  if (!obj) return [];
  for (const key of [
    "groups",
    "children",
    "sections",
    "units",
    "diagrams",
    "items",
  ]) {
    const val = obj[key];
    if (Array.isArray(val)) return val as VinalfaGroup[];
  }
  return [];
}

/** Normalize Vinalfa node flags (hasChild → hasChildren) and nested children. */
function normalizeVinalfaGroup(raw: VinalfaGroup): VinalfaGroup {
  const hasChild =
    (raw as any).hasChild === true ||
    raw.hasChildren === true ||
    (Array.isArray(raw.children) && raw.children.length > 0);
  const children = Array.isArray(raw.children)
    ? raw.children.map(normalizeVinalfaGroup)
    : undefined;
  return {
    ...raw,
    hasChildren: hasChild,
    ...(children ? { children } : {}),
    // Preserve diagram leaf hint from OEM units
    hasDiagram:
      raw.hasDiagram === true ||
      (raw as any).kind === "diagram" ||
      (raw as any).can_fetch_parts === true,
  };
}

export function extractVinalfaList(
  payload: VinalfaGroupsResponse | VinalfaChildrenResponse | null | undefined
): VinalfaGroup[] {
  if (!payload) return [];
  const data = payload.data;
  let result: VinalfaGroup[] = [];
  if (Array.isArray(data)) {
    result = data;
  } else if (data && typeof data === "object") {
    // Observed OEM shape: { data: { catalog, vin, data: { sections|units } } }
    const inner =
      (data as any).data && typeof (data as any).data === "object"
        ? ((data as any).data as Record<string, unknown>)
        : null;
    const fromInner = pickVinalfaArray(inner);
    if (fromInner.length) {
      result = fromInner;
    } else {
      const fromData = pickVinalfaArray(data as Record<string, unknown>);
      if (fromData.length) {
        result = fromData;
      }
    }
  }
  if (!result.length) {
    const top = pickVinalfaArray(payload as Record<string, unknown>);
    if (top.length) {
      result = top;
    }
  }
  return result.map(normalizeVinalfaGroup);
}

export function groupHash(group: VinalfaGroup): string {
  const real =
    group.sectionHash || group.hash || (group as any).section_hash;
  if (real) return String(real);
  // Top-level catalog folders often only have numeric id (no OEM hash)
  if ((group as any).id != null && (group as any).id !== "") {
    return `id:${(group as any).id}`;
  }
  return "";
}

export function groupLabel(group: VinalfaGroup): string {
  return String(
    group.name || group.title || (group as any).label || "Untitled"
  );
}

export function groupImage(group: VinalfaGroup): string | undefined {
  const img =
    group.imageUrl ||
    group.image ||
    (group as any).thumbnail ||
    (group as any).preview;
  return img ? String(img) : undefined;
}

export function extractPartsPayload(payload: VinalfaPartsResponse | null | undefined) {
  const outer =
    payload?.data && typeof payload.data === "object" ? payload.data : payload;
  // Observed: { data: { catalog, sectionName, data: { image, parts, links } } }
  const nested =
    outer &&
    typeof outer === "object" &&
    (outer as any).data &&
    typeof (outer as any).data === "object"
      ? (outer as any).data
      : null;
  const root = nested || outer;
  if (!root || typeof root !== "object") {
    return { image: undefined as string | undefined, hotspots: [] as VinalfaHotspot[], parts: [] as VinalfaPart[] };
  }
  const image =
    (root as any).imageUrl ||
    (root as any).image ||
    (root as any).diagram ||
    undefined;
  const rawParts =
    (root as any).parts ||
    (root as any).positions ||
    [];
  // Flatten Vinalfa parts rows: { reference, numbers:[{number,label,count}], points:[{x,y}] }
  const parts: VinalfaPart[] = [];
  const hotspots: VinalfaHotspot[] = [];
  if (Array.isArray(rawParts)) {
    for (const row of rawParts) {
      if (!row || typeof row !== "object") continue;
      const ref = (row as any).reference ?? (row as any).ref ?? (row as any).position;
      const numbers = (row as any).numbers;
      if (Array.isArray(numbers) && numbers.length) {
        for (const n of numbers) {
          parts.push({
            ref,
            number: n?.number,
            name: n?.label || n?.name,
            qty: n?.count ?? n?.qty,
            replacedBy: n?.replace,
            description: Array.isArray(n?.description)
              ? n.description.map((d: any) => d?.desc || d?.name).filter(Boolean).join(" | ")
              : n?.description,
          });
        }
      } else {
        parts.push(row as VinalfaPart);
      }
      const points = (row as any).points || (row as any).hotspots;
      if (Array.isArray(points)) {
        for (const pt of points) {
          hotspots.push({
            ref,
            x: pt?.x,
            y: pt?.y,
            width: pt?.width,
            height: pt?.height,
            ...pt,
          });
        }
      }
    }
  }
  const extraHotspots =
    (root as any).hotspots ||
    (root as any).coordinates ||
    [];
  if (Array.isArray(extraHotspots)) {
    for (const h of extraHotspots) hotspots.push(h as VinalfaHotspot);
  }
  return {
    image: image ? String(image) : undefined,
    hotspots,
    parts,
  };
}

export function partNumber(part: VinalfaPart): string {
  return String(
    part.number || part.articleNumber || part.oem || ""
  ).trim();
}

/** Normalize OEM refs for matching: "(1)", "01", "1" → "1" */
export function normalizeOemRef(
  value: string | number | null | undefined
): string {
  if (value == null) return "";
  return String(value).replace(/[()]/g, "").replace(/^0+(?=\d)/, "").trim();
}

export function formatOemRef(value: string | number | null | undefined): string {
  const n = normalizeOemRef(value);
  if (!n) return "—";
  if (/^\d+$/.test(n)) return n.padStart(2, "0");
  return n;
}

export function partRef(part: VinalfaPart): string {
  const r = part.ref ?? part.position;
  return r != null ? String(r) : "—";
}

export type OemDiagramSibling = {
  hash: string;
  title: string;
};

export type OemDiagramNavContext = {
  vehicleLabel?: string;
  parentLabel?: string;
  siblings: OemDiagramSibling[];
  currentIndex: number;
};

const OEM_NAV_STORAGE_KEY = "vinalfaOemDiagramNav";

export function saveOemDiagramNav(ctx: OemDiagramNavContext): void {
  try {
    sessionStorage.setItem(OEM_NAV_STORAGE_KEY, JSON.stringify(ctx));
  } catch {
    /* ignore quota / private mode */
  }
}

export function loadOemDiagramNav(): OemDiagramNavContext | null {
  try {
    const raw = sessionStorage.getItem(OEM_NAV_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.siblings)) return null;
    return parsed as OemDiagramNavContext;
  } catch {
    return null;
  }
}

export type VinalfaEquipment = { key?: string; value?: string };

/** Persisted Vinalfa modification for Basic Data fallback on /assemblies */
export type VinalfaVehicleContext = {
  vehicleHash: string;
  vin?: string;
  catalog?: string;
  model?: string;
  engine?: string;
  date?: string;
  transmission?: string;
  equipments?: VinalfaEquipment[];
};

const VINALFA_VEHICLE_CTX_KEY = "vinalfaVehicleContext";

export function saveVinalfaVehicleContext(ctx: VinalfaVehicleContext): void {
  try {
    sessionStorage.setItem(VINALFA_VEHICLE_CTX_KEY, JSON.stringify(ctx));
  } catch {
    /* ignore */
  }
}

export function loadVinalfaVehicleContext(): VinalfaVehicleContext | null {
  try {
    const raw = sessionStorage.getItem(VINALFA_VEHICLE_CTX_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.vehicleHash) return null;
    return parsed as VinalfaVehicleContext;
  } catch {
    return null;
  }
}

export function equipmentValue(
  equipments: VinalfaEquipment[] | undefined,
  ...keys: string[]
): string {
  if (!equipments?.length) return "";
  const lowerKeys = keys.map((k) => k.toLowerCase());
  for (const eq of equipments) {
    const k = String(eq.key || "").toLowerCase();
    if (lowerKeys.some((lk) => k === lk || k.includes(lk))) {
      return String(eq.value || "").trim();
    }
  }
  return "";
}

/** Score TecDoc linkage targets against Vinalfa catalog/model/engine. */
export function pickBestTecdocMatch<T extends Record<string, any>>(
  candidates: T[],
  opts: { catalog?: string; model?: string; engine?: string }
): T | null {
  if (!candidates?.length) return null;
  const catalog = (opts.catalog || "").toLowerCase().trim();
  const modelTokens = (opts.model || "")
    .toLowerCase()
    .split(/[\s/-]+/)
    .filter((t) => t.length >= 2);
  const engine = (opts.engine || "").toLowerCase().trim();

  let best: T | null = null;
  let bestScore = -1;

  for (const c of candidates) {
    let score = 0;
    const hay = [
      c.mfrName,
      c.mfrShortName,
      c.vehicleModelSeriesName,
      c.description,
      ...(Array.isArray(c.engines) ? c.engines.map((e: any) => e?.code) : []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    if (catalog && hay.includes(catalog)) score += 5;
    if (engine) {
      if (hay.includes(engine)) score += 4;
      else if (
        Array.isArray(c.engines) &&
        c.engines.some(
          (e: any) => String(e?.code || "").toLowerCase() === engine
        )
      ) {
        score += 4;
      }
    }
    for (const token of modelTokens) {
      if (hay.includes(token)) score += 2;
    }
    // Prefer passenger-car-like descriptions when present
    if (c.linkageTargetType === "P" || c.linkageTargetType === "passenger") {
      score += 1;
    }
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }

  // If nothing matched meaningfully, still return first candidate
  if (bestScore <= 0) return candidates[0];
  return best;
}

/** Map Vinalfa context into Basic Data row labels for VehicleDetail fallback UI. */
export function mapVinalfaContextToBasicRows(
  ctx: VinalfaVehicleContext
): { title: string; description: string }[] {
  const eq = ctx.equipments || [];
  const power = equipmentValue(eq, "engine power", "engine_power");
  const displacement = equipmentValue(
    eq,
    "engine displacement",
    "engine_displacement"
  );
  const series = equipmentValue(eq, "series");
  const body = equipmentValue(eq, "body", "body type");
  const market = equipmentValue(eq, "market");
  const steering = equipmentValue(eq, "steering");
  const description = [series, body, market, steering].filter(Boolean).join(" · ");

  return [
    { title: "Make", description: ctx.catalog || "" },
    { title: "Model", description: ctx.model || "" },
    { title: "Description", description },
    { title: "Productions years", description: ctx.date || "-" },
    { title: "Engine power [hp]", description: power },
    { title: "Engine power [kw]", description: "" },
    { title: "Cylinders", description: equipmentValue(eq, "engine cylinders", "cylinders") },
    { title: "Capacity", description: displacement },
    { title: "Engine Code", description: ctx.engine || "" },
    { title: "Dve System", description: "" },
    { title: "Fuel Type", description: "" },
    { title: "Body Type", description: body },
    { title: "Transmission", description: ctx.transmission || "" },
  ];
}

export function partQty(part: VinalfaPart): string {
  const q = part.qty ?? part.quantity;
  return q != null ? String(q) : "—";
}

export function partName(part: VinalfaPart): string {
  return String(part.name || part.description || "").trim();
}
